// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/chile/normalize';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import questionImageSrc from './question-mark-icon.svg';
import {NotificationBox, NotificationParagraph, QuestionIcon} from '../../../../atoms/notification-box-chilean';

export function FieldChileanDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    'chileanDocument',
    formatRaw,
    normalizeCustomerDocument,
  );

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('chileanDocument');

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="customer-document">
      <label htmlFor={inputId}>RUT</label>

      <TextField
        as={InputMask}
        id={inputId}
        value={document}
        onChange={handleChangeDocument}
        placeholder="Rol Único Tributario"
        fashion={formHasBeenSent && validationError ? 'error' : null}
        mask={RUT_MASK}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-document"
      />

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
      <NotificationBox>
        <div>
          <QuestionIcon src={questionImageSrc} alt="questionIcon" />
        </div>
        <NotificationParagraph>
          Averigue que el RUT informado en la compra sea el mismo que se utilizará para realizar el pago, para evitar problemas con el procesamiento y la posible cancelación de la transacción.
        </NotificationParagraph>
      </NotificationBox>
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'invalid-rut-pattern':
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>El número es invalido.</ErrorLabel>;
  }
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, RUT_MASK);
}

const RUT_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9K]/i, /[0-9K]/i,
];
